import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { CartProvider } from "./context/CartContext";
import SiteOverridesProvider from "./components/SiteOverridesProvider";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import AccessibilityWidget from "./components/AccessibilityWidget";
import ChatWidget from "./components/ChatWidget";
import Home from "./pages/Home";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import Articles from "./pages/Articles";
import ArticleDetail from "./pages/ArticleDetail";
import Recommendations from "./pages/Recommendations";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Fitness from "./pages/Fitness";
import FitnessProtein from "./pages/FitnessProtein";
import FitnessSupplements from "./pages/FitnessSupplements";
import FitnessEnergy from "./pages/FitnessEnergy";
import FitnessPackages from "./pages/FitnessPackages";
import FitnessDeals from "./pages/FitnessDeals";
import Packages from "./pages/Packages";
import Admin from "./pages/Admin";
import Accessibility from "./pages/Accessibility";
import PromescentPage from "./pages/PromescentPage";
import PartnerProducts from "./pages/PartnerProducts";
import BitPayment from "./pages/BitPayment";
import PayPalPayment from "./pages/PayPalPayment";
import PayBoxPayment from "./pages/PayBoxPayment";
import TeamApp from "./pages/TeamApp";
import NasrallahApp from "./pages/NasrallahApp";
import LiorOrder from "./pages/LiorOrder";
import QuickOrder from "./pages/QuickOrder";
import Wheel from "./pages/Wheel";
import PaymentLink from "./pages/PaymentLink";
import FullFrameBackup from "./pages/FullFrameBackup";
import SideCart from "./components/SideCart";
import Banner from "./components/Banner";
import FreeShippingPopup from "./components/FreeShippingPopup";
import "@/App.css";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Campaign visit counter: any page opened with ?go=1..5 reports a visit for
// that tracking slot (מעקב כניסות לאתר in the dashboard), then cleans the
// param from the address bar.
function TrackGoParam() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const slot = parseInt(params.get('go'), 10);
    if (!slot || slot < 1 || slot > 5) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/slot-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slot }),
    }).catch(() => {});
    params.delete('go');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
  }, []);
  return null;
}

function App() {
  return (
    <SiteOverridesProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <TrackGoParam />
          <AppShell />
        </BrowserRouter>
      </CartProvider>
    </SiteOverridesProvider>
  );
}

function AppShell() {
  const { pathname } = useLocation();
  const isTeamApp = pathname.startsWith('/team');
  // Admin route — hide site Banner/Navbar/Footer/Widgets so the dashboard
  // gets the full viewport width per user request.
  const isAdminApp = pathname.startsWith('/bari');
  // Nasrallah Ibtisaam — separate workspace (own login + Google Calendar).
  const isNasrallah = pathname.startsWith('/nasrallah');
  // LIOR fitness order PWA — standalone installable page, dark gym theme,
  // creates orders with source=lior_link straight into the dashboard.
  const isLior = pathname.startsWith('/lior');
  // Admin quick-order PWA route. Standalone like /lior — minimal chrome.
  const isQuickOrder = pathname.startsWith('/quick-order');
  const isWheel = pathname.startsWith('/wheel');
  const isPaymentLink = pathname.startsWith('/payment-link');

  // Cleanup: if there's a stale service worker registered for the whole domain
  // (from an earlier version that had broad scope), unregister it on non-team pages
  // so /bari and the rest of the site never get served stale cached HTML/JS.
  React.useEffect(() => {
    if (isTeamApp || isNasrallah || isLior) return;
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => {
        const scope = r.scope || '';
        if (!scope.includes('/team') && !scope.includes('/nasrallah') && !scope.includes('/lior')) {
          r.unregister().catch(() => {});
        }
      });
    }).catch(() => {});
  }, [isTeamApp, isNasrallah, isLior]);

  // PWA identity (manifest, title, icons, theme) is set synchronously in
  // public/index.html based on URL path — see the inline <head> bootstrap.
  // Here we only register the Nasrallah service worker (required by Chrome
  // for install eligibility).
  React.useEffect(() => {
    if (!isNasrallah) return;
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/nasrallah-sw.js', { scope: '/nasrallah/' }).catch(() => {});
    }
  }, [isNasrallah]);
  React.useEffect(() => {
    if (!isLior) return;
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/lior-sw.js', { scope: '/lior' }).catch(() => {});
    }
  }, [isLior]);

  if (isTeamApp) {
    return (
      <div className="App min-h-screen">
        <Routes>
          <Route path="/team" element={<TeamApp />} />
          <Route path="/team/:memberId" element={<TeamApp />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (isAdminApp) {
    return (
      <div className="App min-h-screen">
        <Routes>
          <Route path="/bari" element={<Admin />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (isLior) {
    return (
      <div className="App min-h-screen">
        <Routes>
          <Route path="/lior" element={<LiorOrder />} />
        </Routes>
      </div>
    );
  }

  if (isWheel) {
    return (
      <div className="App min-h-screen">
        <Routes>
          <Route path="/wheel" element={<Wheel />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (isQuickOrder) {
    return (
      <div className="App min-h-screen">
        <Routes>
          <Route path="/quick-order" element={<QuickOrder />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (isPaymentLink) {
    return (
      <div className="App min-h-screen">
        <Routes>
          <Route path="/payment-link" element={<PaymentLink />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  if (isNasrallah) {
    return (
      <div className="App min-h-screen">
        <Routes>
          <Route path="/nasrallah/*" element={<NasrallahApp />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  return (
    <div className="App min-h-screen flex flex-col">
      <Banner />
      <Navbar />
      <main className="flex-1 pb-20 lg:pb-0">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/promescent" element={<PromescentPage />} />
          <Route path="/partner" element={<PartnerProducts />} />
          <Route path="/partner/:productId" element={<PartnerProducts />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/fitness" element={<Fitness />} />
          <Route path="/fitness/protein" element={<FitnessProtein />} />
          <Route path="/fitness/supplements" element={<FitnessSupplements />} />
          <Route path="/fitness/energy" element={<FitnessEnergy />} />
          <Route path="/fitness/packages" element={<FitnessPackages />} />
          <Route path="/fitness/deals" element={<FitnessDeals />} />
          <Route path="/packages" element={<Packages />} />
          <Route path="/articles" element={<Articles />} />
          <Route path="/articles/:id" element={<ArticleDetail />} />
          <Route path="/recommendations" element={<Recommendations />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/bari" element={<Admin />} />
          <Route path="/fullframe" element={<FullFrameBackup />} />
          <Route path="/pay/bit" element={<BitPayment />} />
          <Route path="/pay/paypal" element={<PayPalPayment />} />
          <Route path="/pay/paybox" element={<PayBoxPayment />} />
          <Route path="/accessibility" element={<Accessibility />} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppButton />
      <AccessibilityWidget />
      <ChatWidget />
      <SideCart />
      <FreeShippingPopup />
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;